import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { I18NService } from '@core';
import { I18nPipe, ALAIN_I18N_TOKEN } from '@delon/theme';
import { NzAffixModule } from 'ng-zorro-antd/affix';
import { NzBreadCrumbModule } from 'ng-zorro-antd/breadcrumb';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

@Component({
  selector: 'app-report-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    I18nPipe,
    NzTableModule,
    NzCardModule,
    NzInputModule,
    NzButtonModule,
    NzIconModule,
    NzGridModule,
    NzDividerModule,
    NzBreadCrumbModule,
    NzPaginationModule,
    NzTagModule,
    NzToolTipModule,
    NzAffixModule,
    TestIdDirective
  ],
  templateUrl: './report-list.component.html',
  styleUrls: ['./report-list.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportListComponent implements OnInit {
  loading = false;
  pageSize = 20;
  pageIndex = 1;
  total = 0;
  listOfData: any[] = [];
  searchKeywords = '';
  refreshTime = Date.now();
  direction = 'desc';

  private readonly http = inject(HttpService);
  private readonly message = inject(NzMessageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  ngOnInit(): void {
    this.loadData();
  }

  loadData(reset: boolean = false): void {
    if (reset) {
      this.pageIndex = 1;
    }

    this.loading = true;
    const params = {
      key: this.searchKeywords,
      pageSize: this.pageSize,
      pageIndex: this.pageIndex - 1,
      direction: this.direction,
      refreshTime: this.refreshTime
    };

    this.http.post(ConstantUtil.REPORT_LIST, params).subscribe({
      next: (res: any) => {
        if (res.success) {
          // 处理时间戳
          this.listOfData = res.data.items;
          this.total = res.data.totalCount;
          this.cdr.detectChanges();
        } else {
          this.message.error(res.msg ?? this.i18n.fanyi('common.load-failed'));
        }
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.message.error(this.i18n.fanyi('common.load-failed'));
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  search(): void {
    this.refreshTime = Date.now();
    this.loadData(true);
  }

  reset(): void {
    this.searchKeywords = '';
    this.refreshTime = Date.now();
    this.loadData(true);
  }

  // 获取状态文本
  getStatusText(status: string): string {
    const statusMap: { [key: string]: string } = {
      New: this.i18n.fanyi('report.status.pending'),
      Processing: this.i18n.fanyi('report.status.processing'),
      Completed: this.i18n.fanyi('report.status.completed'),
      Rejected: this.i18n.fanyi('report.status.rejected')
    };
    return statusMap[status] || status;
  }

  // 获取状态颜色
  getStatusColor(status: string): string {
    const colorMap: { [key: string]: string } = {
      New: 'blue',
      Processing: 'processing',
      Completed: 'success',
      Rejected: 'error'
    };
    return colorMap[status] ?? 'default';
  }

  // 获取对象类型文本
  getObjectTypeText(type: string): string {
    const typeMap: { [key: string]: string } = {
      Tenant: this.i18n.fanyi('report.object-type.tenant'),
      Employee: this.i18n.fanyi('report.object-type.employee'),
      Contact: this.i18n.fanyi('report.object-type.contact'),
      ServiceNumber: this.i18n.fanyi('report.object-type.service-number'),
      Room: this.i18n.fanyi('report.object-type.room')
    };
    return typeMap[type] || type;
  }

  // 获取对象类型标签颜色
  getObjectTypeColor(type: string): string {
    const colorMap: { [key: string]: string } = {
      Tenant: 'purple',
      Employee: 'blue',
      Contact: 'green',
      ServiceNumber: 'orange',
      Room: 'cyan'
    };
    return colorMap[type] ?? 'default';
  }

  // 显示名称或ID
  getNameOrId(name: string | null, id: string | null): string {
    if (name) return name;
    if (id) return id;
    return '-';
  }

  // 处理页码变化
  pageIndexChange(pageIndex: number): void {
    this.pageIndex = pageIndex;
    this.loadData();
  }

  // 处理每页条数变化
  pageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.loadData();
  }
}
